import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";

import { getDateString, getShiftedDate, toLocalDateKey } from "../../../utils/datetime";
import {
  getDayDetails,
  duplicateScopes,
} from "../../../services/api/dispatch";
import {
  loadPlanningWeatherSplitByDay,
  patchScopeAspects,
  updateScope as apiUpdateScope,
} from "../../../services/api/planning";
import { getCurrentUser } from "../../../services/api/core";
import { usePlanningSyncPoller } from "../../../features/planning/sync/usePlanningSyncPoller";
import { useDeleteScopeBlock } from "../../../features/planning/shared/useDeleteScopeBlock";
import useDispatchFilters from "../../../features/planning/dailyDispatch/useDispatchFilters";
import {
  getDispatchUnavailableWorkerIdsForUnskip,
  getWorkerIdsExcluding,
} from "../../../features/planning/shared/planningWorkerConflicts";
import { getWorkerDisplayLabel } from "../../../utils/business";

import { useWeeklyContext } from "../../global/WeeklyContext";
import { usePlanningContext } from "./PlanningContext";

/**
 * Context that is SPECIFIC to the Dispatch app. Anything generic should be in higher
 * level context like CoreContext.
 *
 * @context DispatchContext
 *
 * @typedef {Object} DispatchContextType
 */
export const DispatchContext = createContext(null);

/**
 * Read DispatchContext. Throws if used outside DispatchProvider.
 *
 * @returns {Object}
 */
export function useDispatchContext() {
  const context = useContext(DispatchContext);
  if (!context) {
    throw new Error(
      "useDispatchContext must be used within a DispatchProvider"
    );
  }
  return context;
}

/**
 * Provide Dispatch page state and handlers for Daily Dispatch and embeds.
 *
 * @param {Object} props
 * @param {import('react').ReactNode} props.children
 * @param {boolean} [props.compact=false] Whether to use compact dispatch chrome.
 * @param {boolean} [props.readOnly=false] Whether mutation UI/actions are disabled.
 * @param {Object|null} [props.singleProject=null] Optional project used to scope dispatch data.
 * @param {boolean} [props.detailsOnly=false] Whether to lock job rows into details view.
 * @param {Object} [props.options] Additional dispatch controller options.
 * @returns {JSX.Element}
 */
export function DispatchProvider({
  children,
  compact = false,
  readOnly = false,
  singleProject = null,
  detailsOnly = false,
  options = {},
}) {
  const value = useDispatchCtx({
    ...options,
    compact: options.compact ?? compact,
    readOnly: options.readOnly ?? readOnly,
    singleProject: options.singleProject ?? singleProject,
    detailsOnly: options.detailsOnly ?? detailsOnly,
  });

  return (
    <DispatchContext.Provider value={value}>{children}</DispatchContext.Provider>
  );
}

/**
 * Daily dispatch week state: scopes, day details, weather, and assignment mutations.
 *
 * @param {Object} [options]
 * @param {boolean} [options.compact=false] Hide summary-bar chrome.
 * @param {boolean} [options.readOnly=false] Block mutations.
 * @param {Object|null} [options.singleProject=null] Limit to one job.
 * @param {boolean} [options.detailsOnly=false]
 * @param {boolean} [options.loadDayDetails=true] Load DayDetails even in compact (locks/conflicts).
 * @param {boolean} [options.loadWeather] Defaults to `!compact`.
 * @returns {Object} Dispatch context value.
 */
export function useDispatchCtx(options = {}) {
  const {
    compact = false,
    readOnly: readOnlyOverride = false,
    singleProject = null,
    detailsOnly = false,
    // DayDetails power locks/conflicts even when SummaryBar UI is hidden by compact.
    loadDayDetails = true,
    loadWeather = !compact,
  } = options;
  const {
    weekStart,
    reloadScopes,
    applyScopePatch,
    scopes,
    jobs,
    datesOfWeek,
  } = useWeeklyContext();
  const {
    scopeObjects,
    workers,
    modelReferenceData,
    reloadModelReferenceData,
    notify,
    runPlanningOperation,
    clientInstanceId,
    setPlanningLastUpdatedFromEvent,
    isScopeObjectsLoading,
    isWorkersLoading,
    isModelReferenceLoading,
  } = usePlanningContext();

  /**
   * Dispatch filters panel state: project/scope/worker multi-selects,
   * empty-project visibility, and the derived filtered jobs/scopes.
   */
  const dispatchFilters = useDispatchFilters({
    jobs,
    scopes,
    datesOfWeek,
    singleProject,
  });

  // Global loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(Boolean(readOnlyOverride));
  const renderCount = useRef(0);

  // Global capture states to enable mounting of capture components (for performance)
  const [isDispatchCapturing, setIsDispatchCapturing] = useState(false);
  const [isJobReportCapturing, setIsJobReportCapturing] = useState(false);
  // Global state for capture components to use to set their own state
  const [jobReportDate, setJobReportDate] = useState(null);

  const [isJobsLoading] = useState(true);
  const [isScopeMutationPending, setIsScopeMutationPending] = useState(false);
  const [scopeActionMenu, setScopeActionMenu] = useState(null);

  /** Open the shared Dispatch WorkScopeAspect action menu at a card/button anchor. */
  const openScopeActionMenu = useCallback(({ scope, date, anchorRect, disableCopy = false }) => {
    setScopeActionMenu({
      scope,
      date,
      anchorRect,
      disableCopy,
      openKey: Date.now(),
    });
  }, []);

  /**
   * After a shared scope-block delete, drop the card menu and refresh weekly scopes.
   *
   * @param {void}
   * @returns {void}
   */
  const handleScopeBlockDeleted = useCallback(() => {
    setScopeActionMenu(null);
    reloadScopes();
  }, [reloadScopes]);

  const {
    deleteScopeBlockModal,
    deleteScopeBlockBusy,
    requestDeleteScopeBlock,
    closeDeleteScopeBlockModal,
    confirmDeleteScopeBlock,
  } = useDeleteScopeBlock({
    runPlanningOperation,
    onDeleted: handleScopeBlockDeleted,
  });

  /** Dispatch-only reference data is loaded lazily so Schedule does not pay for it on first render. */
  useEffect(() => {
    if (modelReferenceData == null && !isModelReferenceLoading) {
      void reloadModelReferenceData();
    }
  }, [isModelReferenceLoading, modelReferenceData, reloadModelReferenceData]);

  useEffect(() => {
    if (
      !isJobsLoading &&
      !isScopeObjectsLoading &&
      !isWorkersLoading &&
      !isModelReferenceLoading &&
      !isScopeMutationPending
    ) {
      renderCount.current += 1;
      if (renderCount.current > 1) {
        setIsLoading(false);
      }
    }
  }, [
    isJobsLoading,
    isScopeObjectsLoading,
    isWorkersLoading,
    isModelReferenceLoading,
    isScopeMutationPending,
  ]);

  // Current user data — forced readOnly embeds skip role lookup.
  useMemo(() => {
    if (readOnlyOverride) {
      setIsReadOnly(true);
      return;
    }
    /**
     * Loads the current user to decide if dispatch is read-only (non office roles).
     *
     * @returns {Promise<void>}
     */
    async function fetchcurrentUser() {
      const response = await getCurrentUser();
      setIsReadOnly(
        !(
          response.user.executive ||
          response.user.manager ||
          response.user.staff
        )
      ); // Set readOnly state based on user roles
      // setIsReadOnly(true); // For testing
    }
    fetchcurrentUser();
  }, [readOnlyOverride]); // Run only once on page load as log in/out will trigger a page reload anyway

  const [allJobsEdting, setAllJobsEditing] = useState(false);

  // Day Details Data
  const [dayDetails, setDayDetails] = useState(null);
  const reloadDays = useCallback(async () => {
    if (!loadDayDetails) {
      setDayDetails(null);
      return;
    }
    setIsLoading(true);
    try {
      const rangeStart = toLocalDateKey(weekStart);
      const rangeEnd = toLocalDateKey(getShiftedDate(weekStart, 6));
      const details = await getDayDetails(rangeStart, rangeEnd);
      setDayDetails(details);
    } catch (e) {
      console.error(`could not get day details from ${weekStart}`, e);
    } finally {
      setIsLoading(false);
    }
  }, [weekStart, loadDayDetails]);
  useMemo(() => {
    reloadDays();
  }, [reloadDays]);

  const [dispatchWeatherByDate, setDispatchWeatherByDate] = useState({});
  const reloadDispatchWeatherForRange = useCallback(async (startIso, endIso) => {
    // Clear stale rows so days show loading until their segment lands.
    setDispatchWeatherByDate({});
    if (!loadWeather) {
      return;
    }
    try {
      // Past (cache-only) resolves quickly; future may wait on the live forecast.
      await loadPlanningWeatherSplitByDay(startIso, endIso, (segment) => {
        setDispatchWeatherByDate((prev) => ({ ...prev, ...segment }));
      });
    } catch (e) {
      console.error("Dispatch weather load failed", e);
    }
  }, [loadWeather]);
  const getDispatchWeather = useCallback(
    (dateKey) => dispatchWeatherByDate[dateKey] ?? null,
    [dispatchWeatherByDate]
  );

  useEffect(() => {
    if (!loadWeather) {
      setDispatchWeatherByDate({});
      return;
    }
    const rangeStart = toLocalDateKey(weekStart);
    const rangeEnd = toLocalDateKey(getShiftedDate(weekStart, 6));
    void reloadDispatchWeatherForRange(rangeStart, rangeEnd);
  }, [reloadDispatchWeatherForRange, weekStart, loadWeather]);

  // Scope states
  const [copyScopeOrigin, setCopyScopeOrigin] = useState(null);
  const [copyScopesDate, setCopyScopesDate] = useState(null);
  const [scopesToDelete, setScopesToDelete] = useState([]);
  const [workerUnskipConflictModal, setWorkerUnskipConflictModal] = useState(null);

  const pasteScopes = useCallback(
    async (type, pasteTarget, pasteDate) => {
      setIsScopeMutationPending(true);
      try {
        const origin =
          type === "day"
            ? getDateString(copyScopesDate)
            : type === "job"
              ? copyScopeOrigin
              : null;
        await runPlanningOperation({
          module: "dispatch",
          operation: "duplicateScopes",
          resources: ["dispatch.aspects"],
          apiCall: () => duplicateScopes(type, origin, pasteTarget, pasteDate),
        });
      } catch (e) {
        console.error("could not create scope", e);
      } finally {
        reloadScopes();
        // Uncommment this to clear the copy state after pasting
        // type === "job" ? setCopyScopeOrigin(null) : setCopyScopesDate(null);
        setIsScopeMutationPending(false);
      }
    },
    [copyScopeOrigin, copyScopesDate, reloadScopes, runPlanningOperation]
  );

  /** Convert a Dispatch scope patch into the notification operation key. */
  const getScopeUpdateOperation = useCallback((newData) => {
    if (newData && typeof newData === "object" && !(newData instanceof FormData)) {
      if (Object.prototype.hasOwnProperty.call(newData, "skipped")) {
        return newData.skipped ? "setScopeSkipped" : "removeScopeSkipped";
      }
      if (Object.prototype.hasOwnProperty.call(newData, "skipped_reason")) {
        return newData.skipped_reason
          ? "updateScopeSkippedReason"
          : "clearScopeSkippedReason";
      }
      if (Object.prototype.hasOwnProperty.call(newData, "notes")) {
        return "updateScopeNotes";
      }
      if (Object.prototype.hasOwnProperty.call(newData, "extra_data_sections")) {
        return "updateScopeExtraNotes";
      }
    }
    return "updateScope";
  }, []);

  const updateScope = useCallback(
    async (scopeId, newData) => {
      setIsScopeMutationPending(true);
      try {
        await runPlanningOperation({
          module: "dispatch",
          operation: getScopeUpdateOperation(newData),
          resources: ["dispatch.aspects"],
          entityIds: [scopeId],
          apiCall: () => apiUpdateScope(scopeId, newData, "dispatch"),
        });
        applyScopePatch(scopeId, newData);
      } catch (e) {
        console.error(`could not update scope ${scopeId}`, e);
      } finally {
        setIsScopeMutationPending(false);
      }
    },
    [applyScopePatch, getScopeUpdateOperation, runPlanningOperation]
  );

  /** Persist a Dispatch unskip and optionally strip unavailable workers from its crew. */
  const applyDispatchUnskip = useCallback(
    async ({ scope, filteredWorkerIds, filteredAssignment, crewChanged }) => {
      setIsScopeMutationPending(true);
      try {
        await runPlanningOperation({
          module: "dispatch",
          operation: "removeScopeSkipped",
          resources: ["dispatch.aspects"],
          entityIds: [scope.id],
          apiCall: () => apiUpdateScope(scope.id, { skipped: false }, "dispatch"),
        });
        applyScopePatch(scope.id, { skipped: false });

        if (crewChanged) {
          await runPlanningOperation({
            module: "dispatch",
            operation: "removeScopeSkippedUnavailableWorkers",
            resources: ["dispatch.aspects"],
            entityIds: [scope.id],
            apiCall: () => patchScopeAspects(scope.id, filteredWorkerIds, "dispatch"),
          });
          applyScopePatch(scope.id, { worker_assignment: filteredAssignment });
        }
      } catch (e) {
        console.error(`could not unskip scope ${scope.id}`, e);
      } finally {
        setIsScopeMutationPending(false);
      }
    },
    [applyScopePatch, runPlanningOperation]
  );

  /**
   * Toggle a Dispatch WorkScopeAspect skipped flag. Unskipping checks active same-day
   * assignments and off-work workers, then asks before stripping unavailable workers.
   */
  const toggleScopeSkipped = useCallback(
    async (scope) => {
      if (!scope?.id) {
        return;
      }
      const nextSkipped = !Boolean(scope.skipped);
      if (nextSkipped) {
        await updateScope(scope.id, { skipped: true });
        return;
      }

      const unavailableWorkerIds = getDispatchUnavailableWorkerIdsForUnskip({
        scope,
        scopes,
        dayDetails,
      });
      const filteredWorkerIds = getWorkerIdsExcluding(
        scope.worker_assignment,
        unavailableWorkerIds
      );
      const currentWorkerIds = (scope.worker_assignment ?? [])
        .map((worker) => Number(worker?.id))
        .filter((id) => Number.isFinite(id));
      const crewChanged =
        currentWorkerIds.length !== filteredWorkerIds.length ||
        currentWorkerIds.some((id, index) => id !== filteredWorkerIds[index]);
      const filteredAssignment = (scope.worker_assignment ?? []).filter((worker) =>
        filteredWorkerIds.includes(Number(worker?.id))
      );

      if (crewChanged) {
        const removedWorkerNames = (scope.worker_assignment ?? [])
          .filter((worker) => unavailableWorkerIds.has(Number(worker?.id)))
          .map(getWorkerDisplayLabel);
        setWorkerUnskipConflictModal({
          scope,
          filteredWorkerIds,
          filteredAssignment,
          crewChanged,
          summaries: [
            {
              dateKey: scope.date,
              conflictNames: removedWorkerNames,
            },
          ],
        });
        return;
      }

      await applyDispatchUnskip({
        scope,
        filteredWorkerIds,
        filteredAssignment,
        crewChanged,
      });
    },
    [
      applyDispatchUnskip,
      dayDetails,
      scopes,
      updateScope,
    ]
  );

  /** Confirm Dispatch unskip conflict stripping from the global modal. */
  const confirmWorkerUnskipConflictModal = useCallback(async () => {
    const pending = workerUnskipConflictModal;
    if (!pending) {
      return;
    }
    setWorkerUnskipConflictModal(null);
    await applyDispatchUnskip(pending);
  }, [applyDispatchUnskip, workerUnskipConflictModal]);

  /** Cancel Dispatch unskip when unavailable workers would need to be removed. */
  const cancelWorkerUnskipConflictModal = useCallback(() => {
    setWorkerUnskipConflictModal(null);
  }, []);

  // Global Worker modal state management
  const [isWorkerModalOpen, setIsWorkerModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState({});

  /**
   * Opens the global worker assignment modal with the given props.
   *
   * @param {Object} props Props forwarded to the worker modal.
   * @returns {void}
   */
  const openWorkerModal = (props) => {
    setModalProps(props);
    setIsWorkerModalOpen(true);
  };

  /**
   * Closes the global worker assignment modal and clears its props.
   *
   * @returns {void}
   */
  const closeWorkerModal = () => {
    setIsWorkerModalOpen(false);
    setModalProps({});
  };

  // Global Worker modal handlers
  const updateScopeWorkers = useCallback(
    async (scopeId, newWorkerIds) => {
      setIsWorkerModalOpen(false);
      await runPlanningOperation({
        module: "dispatch",
        operation: "updateScopeWorkers",
        resources: ["dispatch.aspects"],
        entityIds: [scopeId],
        apiCall: () => patchScopeAspects(scopeId, newWorkerIds, "dispatch"),
      });
      await reloadScopes();
    },
    [reloadScopes, runPlanningOperation]
  );

  const handleWorkerModalSubmit = useCallback(
    (
      scopeId,
      selectedWorkerIDs,
      submittedWorkerIds,
      setPrevWorkerIds,
      setSelectedWorkerIds
    ) => {
      if (setPrevWorkerIds) {
        setPrevWorkerIds(selectedWorkerIDs);
      }
      if (setSelectedWorkerIds) {
        setSelectedWorkerIds(submittedWorkerIds);
      }
      updateScopeWorkers(scopeId, submittedWorkerIds);
    },
    [updateScopeWorkers]
  );

  const syncReloadByResource = useMemo(
    () => ({
      "schedule.aspects": reloadScopes,
      "dispatch.aspects": reloadScopes,
      "dispatch.day_details": reloadDays,
      "dispatch.dispatch_stage": reloadDays,
    }),
    [reloadDays, reloadScopes]
  );

  usePlanningSyncPoller({
    currentModule: "dispatch",
    clientInstanceId,
    notify,
    onLatestEvent: setPlanningLastUpdatedFromEvent,
    reloadByResource: syncReloadByResource,
  });

  const contextValue = useMemo(
    // Memo so if another component calls it doesn't re-run context unless changed
    () => ({
      ...dispatchFilters,
      compact,
      readOnly: isReadOnly,
      singleProject,
      detailsOnly,
      isLoading,
      isJobsLoading,
      allJobsEdting,
      setAllJobsEditing,
      scopeObjects,
      scopesToDelete,
      setScopesToDelete,
      copyScopeOrigin,
      setCopyScopeOrigin,
      copyScopesDate,
      setCopyScopesDate,
      reloadDays,
      workers,
      pasteScopes,
      updateScope,
      toggleScopeSkipped,
      workerUnskipConflictModal: workerUnskipConflictModal?.summaries ?? null,
      confirmWorkerUnskipConflictModal,
      cancelWorkerUnskipConflictModal,
      runPlanningOperation,
      deleteScopeBlockModal,
      deleteScopeBlockBusy,
      requestDeleteScopeBlock,
      closeDeleteScopeBlockModal,
      confirmDeleteScopeBlock,
      dayDetails,
      setDayDetails,
      dispatchWeatherByDate,
      getDispatchWeather,
      modelReferenceData,
      reloadModelReferenceData,
      isWorkerModalOpen,
      openWorkerModal,
      closeWorkerModal,
      setIsWorkerModalOpen,
      modalProps,
      handleWorkerModalSubmit,
      updateScopeWorkers,
      isDispatchCapturing,
      setIsDispatchCapturing,
      isJobReportCapturing,
      setIsJobReportCapturing,
      jobReportDate,
      setJobReportDate,
      isReadOnly,
      setIsReadOnly,
      scopeActionMenu,
      openScopeActionMenu,
    }),
    [
      dispatchFilters,
      compact,
      singleProject,
      detailsOnly,
      isLoading,
      isJobsLoading,
      allJobsEdting,
      setAllJobsEditing,
      scopeObjects,
      scopesToDelete,
      setScopesToDelete,
      copyScopeOrigin,
      setCopyScopeOrigin,
      copyScopesDate,
      setCopyScopesDate,
      reloadDays,
      workers,
      pasteScopes,
      updateScope,
      toggleScopeSkipped,
      workerUnskipConflictModal,
      confirmWorkerUnskipConflictModal,
      cancelWorkerUnskipConflictModal,
      runPlanningOperation,
      deleteScopeBlockModal,
      deleteScopeBlockBusy,
      requestDeleteScopeBlock,
      closeDeleteScopeBlockModal,
      confirmDeleteScopeBlock,
      dayDetails,
      setDayDetails,
      dispatchWeatherByDate,
      getDispatchWeather,
      modelReferenceData,
      reloadModelReferenceData,
      isWorkerModalOpen,
      setIsWorkerModalOpen,
      modalProps,
      handleWorkerModalSubmit,
      updateScopeWorkers,
      isDispatchCapturing,
      setIsDispatchCapturing,
      isJobReportCapturing,
      setIsJobReportCapturing,
      jobReportDate,
      setJobReportDate,
      isReadOnly,
      setIsReadOnly,
      scopeActionMenu,
      openScopeActionMenu,
    ]
  );

  return {
    ...contextValue,
  };
}

export default useDispatchCtx; // Export the hook as default for backward compatibility
